<template>
  <div class="min-h-[10rem]">
    <!-- Пустое состояние -->
    <div
      v-if="!isLoading && !places.length"
      class="bg-white rounded-3xl max-md:rounded-2xl flex flex-col items-center justify-center gap-3 py-12 text-center"
    >
      <div class="size-14 rounded-full bg-bgnew flex items-center justify-center">
        <Icon name="lucide:inbox" class="size-7 text-txprimary/30" />
      </div>
      <div class="text-txprimary/60 text-sm">У этого владельца пока нет других каналов</div>
    </div>

    <!-- Карточки каналов владельца (как в каталоге).
         На мобильных — вертикальная карточка: десктопная в экран не помещается. -->
    <div v-else class="flex flex-col gap-3">
      <component
        :is="isWide ? CatalogPlaceCard : CatalogPlaceCardMobile"
        v-for="place in places"
        :key="place.id"
        :place="place"
        :is-in-basket="!!findBasketItem(place)"
        :basket-quantity="findBasketItem(place)?.quantity || 0"
        :buttons-color="accentColor"
        :compact="compact"
        @open-place="openPlace"
        @add-to-basket="addToBasket"
        @decrease-quantity="decreaseQuantity"
        @increase-quantity="increaseQuantity"
        @follow="onFollow"
        @message="emit('message', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import PublisherService from "~/services/PublisherService";
  import PlaceService from "~/services/PlaceService";
  import BasketService from "~/services/BasketService";
  import { useBasketStore } from "~/store/BasketModule";
  import { useUserStore } from "~/store/UserModule";
  import { useWindowSize } from "@vueuse/core";
  import CatalogPlaceCard from "~/components/catalog/PlaceCard.vue";
  import CatalogPlaceCardMobile from "~/components/catalog/PlaceCardMobile.vue";
  import type { ReducedPlace } from "~/interfaces/publisher";

  // Десктопная карточка широкая и помещается только в полноразмерный контейнер /ch
  // (max-w-971px). Ниже 1024px она обрезается — показываем вертикальную мобильную.
  const { width } = import.meta.client ? useWindowSize() : { width: ref(1280) };
  const isWide = computed(() => width.value >= 1024);

  const props = defineProps<{
    userId?: number;
    placeId?: number;
    accentColor?: string;
    // Панель канала в каталоге: узкая карточка со статистикой в два ряда
    compact?: boolean;
  }>();

  const emit = defineEmits<{ message: [place: ReducedPlace] }>();

  const userModule = useUserStore();
  const { authorized } = storeToRefs(userModule);
  const basketStore = useBasketStore();
  const { basket } = storeToRefs(basketStore);

  const places = ref<any[]>([]);
  const isLoading = ref<boolean>(true);

  const getAnotherPlaces = async () => {
    if (!props.userId || !props.placeId) {
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    try {
      const res: any = await PublisherService.getPublisherActivePlaces(props.userId, props.placeId);
      const records: any[] = Array.isArray(res) ? res : res?.records || [];
      // Карточке нужен выбранный формат (v-model селекта) — по умолчанию первый прайс.
      places.value = records.map((place: any) => ({
        ...place,
        prices: place.prices || [],
        format: place.prices?.[0] || null,
      }));
    } finally {
      isLoading.value = false;
    }
  };

  const findBasketItem = (place: any) =>
    basket.value.find(
      (item: any) =>
        item.id === place.id &&
        (item.format?.priceType === place.format?.priceType || (!item.format && !place.format))
    );

  const openPlace = (slug: string) => {
    navigateTo(`/ch/${slug}`);
  };

  const addToBasket = (place: any) => {
    const existing = findBasketItem(place);
    if (existing) {
      existing.quantity = (existing.quantity || 1) + 1;
      return;
    }

    // Независимая копия — чтобы не тянуть в корзину циклические ссылки
    const basketItem = JSON.parse(
      JSON.stringify({
        id: place.id,
        name: place.name,
        url: place.url,
        avatar: place.avatar,
        format: place.format,
        quantity: 1,
        categories: place.categories,
        subscribersStatistic: place.subscribersStatistic,
        prices: place.prices,
        viewsStatistic: place.viewsStatistic,
        er: place.er,
      })
    );

    BasketService.addPlace(basketItem);
    basket.value.push(basketItem as any);
  };

  const increaseQuantity = (place: any) => {
    const basketItem = findBasketItem(place);
    if (basketItem && basketItem.quantity && basketItem.quantity < 10) basketItem.quantity++;
  };

  const decreaseQuantity = (place: any) => {
    const basketItem = findBasketItem(place);
    if (basketItem && basketItem.quantity && basketItem.quantity > 1) {
      basketItem.quantity--;
      return;
    }
    basket.value = basket.value.filter(
      (item: ReducedPlace) =>
        !(item.id === place.id && item.format?.priceType === place.format?.priceType)
    );
    if (place.format) BasketService.removePlace(place.id, place.format);
  };

  const onFollow = async (place: any) => {
    if (!authorized.value) {
      userModule.loginDialogVisible = true;
      return;
    }
    try {
      if (place.isFavorite) {
        await PlaceService.removePlaceFromFavorite({
          id: place.id,
          social: place.social || "telegram",
        });
        place.isFavorite = false;
      } else {
        await PlaceService.addPlaceToFavorite({
          placeId: place.id,
          social: place.social || "telegram",
        });
        place.isFavorite = true;
      }
    } catch (error) {
      console.error(error);
    }
  };

  watch(
    () => [props.userId, props.placeId],
    () => getAnotherPlaces()
  );

  onMounted(() => getAnotherPlaces());
</script>
